import {
  setupDropzone,
  attachDropzoneSuccessListener,
} from './controllers/dropzone_controller';

import { setupAuthentication, sendAuthenticatedRequest } from './auth.js';
import stateManager from './data/state_manager.js';
import { FormPopupController } from './controllers/popups/form_popup_controller';
import { fetchStreamingData } from './data/streaming.js';
import { CardGrid } from './ui/card_grid';
import { LOG_LEVELS, logMessage } from './logging.js';
import config from '../config';
import { showOverlay, hideOverlay } from './ui_components/overlay';
import {
  extractIdFromUrl,
  getSubscriptionLevel,
  getUser,
  updateOnboardingStatus,
  updateCollectionPrivacy,
  updateCollectionName,
  createCollection,
  updateTourStatus,
  getCollections,
  deleteCollection,
  setDefaultCollection,
} from './data/update_operations';
import { FixCornersController } from './controllers/popups/fix_corners_controller';
import { canUseMultiCollection } from './utils/subscription_utils.js';
import './navbar.js';
import { FeatureTour } from './feature_tour';
import { fetchCardData } from './data/update_operations';
import { shareLink, copyLink } from './utils/share_utils.js';
import { showProfileSetupWizardAndWait } from './ui/profile_setup_wizard.js';
import { MobileCameraCapture } from './ui/mobile_camera_capture.js';

window.Dropzone.autoDiscover = false;

let abortControllers = [];
let dropzone_manager1 = null;
let mobileCameraCapture = null;

let form_popup_controller; // will be initialized after cardGrid

let processingErrorToastElements = null;
let processingErrorToastHideTimeoutId = null;
let processingErrorToastKeydownHandler = null;

function getDesktopTourStartSteps() {
  return [
    {
      elementId: 'desktop_dropzone_container',
      title: 'Scan the first card from your collection',
      disableButtons: false, // Updated property name
      skipOnly: true,
      description:
        'Drag in a clear photo of a card you own to start your collection. <a href="#" class="help-link">See scanning tips</a>.',
      position: 'below', // or 'below', etc.
      linkSelector: '.help-link', // Selector for the link in the description
      callback: () => setupOnboardingModal(),
      endTour: false,
    },
  ];
}

function getMobileTourStartSteps() {
  return [
    {
      elementId: 'plus-icon',
      title: 'Scan the first card from your collection',
      disableButtons: false, // Updated property name
      skipOnly: true,
      description:
        "Tap <strong>+</strong> to photograph a card you own. <a href=\"#\" class=\"help-link\">See scanning tips</a>.",
      position: 'above', // or 'below', etc.
      linkSelector: '.help-link', // Selector for the link in the description
      callback: () => setupOnboardingModal(),
      endTour: false,
    },
  ];
}

// Method to define steps with direct callbacks
function getSteps() {
  return [
    {
      elementId: 'colleciton_title_container',
      title: 'Say hello to your collection.',
      disableButtons: false, // Updated property name
      description: 'Use the gear to manage and share your collection.',
      position: 'below', // or 'below', etc.
      endTour: false,
      skipOnly: false,
    },
    {
      elementId: 'card_0',
      title: 'Check out your card',
      disableButtons: true, // Updated property name
      description: 'Click a card for actions.',
      position: 'below', // or 'below', etc.
      endTour: false,
      skipOnly: false,
    },
    {
      elementId: 'popup_menu_container',
      title: 'Discover what you can do.',
      disableButtons: false, // Updated property name
      description: 'Use the menu to manage cards or list on eBay. That\'s it!',
      position: 'below', // or 'below', etc.
      endTour: true,
      skipOnly: false,
    },
    // Add more steps as needed
  ];
}

const tour = new FeatureTour(getSteps(), window, false);
const startTour = new FeatureTour(getDesktopTourStartSteps(), window, true);
const startTourMobile = new FeatureTour(
  getMobileTourStartSteps(),
  window,
  true
);

const cardGrid = new CardGrid(tour);
form_popup_controller = new FormPopupController(cardGrid);

const DESKTOP_TOUR_STEP_INDEX = 0;
const DESKTOP_TOUR_DEFAULT_ELEMENT_ID = 'desktop_dropzone_container';
const DESKTOP_TOUR_WORKSPACE_ELEMENT_ID = 'desktop_dropzone_workspace';

document.addEventListener('DOMContentLoaded', async function () {
  try {
    await setupAuthentication(
      handleAuthenticatedUser,
      handleUnauthenticatedUser
    );
    logMessage(LOG_LEVELS.INFO, 'Authentication setup complete.');
    setupSearchFilter();
  } catch (error) {
    logMessage(LOG_LEVELS.ERROR, 'Error setting up authentication', {
      error: error.message,
    });
  }
});

function setupUserToken(token) {
  window.userToken = token;
  logMessage(LOG_LEVELS.INFO, 'User token setup complete.');
  return { Authorization: `Bearer ${window.userToken}` };
}

function getUserToken() {
  return { Authorization: `Bearer ${window.userToken}` };
}

function setupOnboardingModal() {
  // Retrieve user token headers
  const headers = getUserToken();

  // Select DOM elements
  const closeBtn = document.getElementById('closeOnboarding');
  const tips = document.querySelectorAll('.tip-item');
  const imagesIntro = document.querySelector('.images-intro');
  const exampleImages = document.querySelector('.example-images');
  const imageContainers = document.querySelectorAll(
    '.examples-image-container'
  );
  const onboardingModal = document.getElementById('onboardingModal');

  // **Check if all elements exist**
  if (
    !closeBtn ||
    !tips.length ||
    !imagesIntro ||
    !exampleImages ||
    !imageContainers.length ||
    !onboardingModal
  ) {
    console.error('Onboarding modal elements are missing from the DOM.');
    return;
  }

  // **1. Reset Close Button State**
  closeBtn.disabled = false;
  closeBtn.style.opacity = '1';

  // **2. Prevent Multiple Event Listeners**
  // Remove existing event listeners by cloning (if not implementing the flag method)
  const newCloseBtn = closeBtn.cloneNode(true);
  closeBtn.parentNode.replaceChild(newCloseBtn, closeBtn);

  // Reassign closeBtn to the new cloned button
  const updatedCloseBtn = document.getElementById('closeOnboarding');

  // **3. Add Event Listener Once**
  updatedCloseBtn.addEventListener('click', () => {
    onboardingModal.style.display = 'none';

    updateOnboardingStatus(headers);
    document.body.classList.remove('body-no-scroll');
  });

  // **4. Initialize Modal State**
  document.body.classList.add('body-no-scroll');

  // **5. Disable the Close Button Initially**
  updatedCloseBtn.disabled = true;
  updatedCloseBtn.style.opacity = '0.5'; // Optional: visually indicate the button is disabled

  // **6. Define Delay Constants**
  const TIP_DISPLAY_DURATION = 1250; // in milliseconds
  const IMAGES_INTRO_DELAY = 50;
  const IMAGES_CONTAINER_DELAY = 500;
  const ADDITIONAL_BUFFER = 50;
  const EXTRA_DELAY = 1000;

  // **7. Calculate Dynamic Transition Durations**
  const totalTipsTime = tips.length * TIP_DISPLAY_DURATION;
  const totalDelay =
    totalTipsTime +
    IMAGES_INTRO_DELAY +
    IMAGES_CONTAINER_DELAY +
    ADDITIONAL_BUFFER +
    EXTRA_DELAY;

  // **8. Reset Modal Content Opacity**
  tips.forEach((tip) => {
    tip.style.opacity = '0';
  });
  imagesIntro.style.opacity = '0';
  exampleImages.style.opacity = '0';
  imageContainers.forEach((container) => {
    container.style.opacity = '0';
  });

  // **9. Animate the Tips One by One**
  tips.forEach(function (tip, index) {
    setTimeout(
      function () {
        tip.style.opacity = '1';

        // If this is the last tip, start showing the images
        if (index === tips.length - 1) {
          // Show the images intro text
          setTimeout(
            function () {
              imagesIntro.style.opacity = '1';

              // After intro text, show the example images together
              setTimeout(function () {
                exampleImages.style.opacity = '1';

                // Show both images together without delay
                imageContainers.forEach(function (container) {
                  container.style.opacity = '1';
                });
              }, IMAGES_CONTAINER_DELAY); // Delay before showing images container
            },
            totalTipsTime / tips.length + IMAGES_INTRO_DELAY
          ); // Delay before showing images intro text
        }
      },
      index * (totalTipsTime / tips.length)
    ); // Delay between tips
  });

  // **10. Enable the Close Button After All Animations Are Done**
  setTimeout(function () {
    updatedCloseBtn.disabled = false;
    updatedCloseBtn.style.opacity = '1'; // Reset the button's appearance
  }, totalDelay);

  // **11. Show the Modal**
  onboardingModal.style.display = 'flex';
}
// Function to show the success checkmark
function showSuccessIcon(message) {
  const successMessageDiv = document.getElementById('success-feedback');
  const successMessageSpan = document.getElementById('success-message');

  if (message) {
    successMessageSpan.textContent = message;
  }

  successMessageDiv.classList.add('visible');

  // Hide the checkmark after 2 seconds
  setTimeout(() => {
    successMessageDiv.classList.remove('visible');
  }, 2000); // 2 seconds
}

// Function to show the error message
function showErrorIcon(message) {
  let errorMessageDiv = document.getElementById('error-feedback');
  let errorMessageSpan = document.getElementById('error-message');

  // If the existing error feedback is not present or hidden (e.g., inside a closed modal),
  // create a global fallback container so messages can still be displayed.
  if (!errorMessageDiv || errorMessageDiv.offsetParent === null) {
    errorMessageDiv = document.getElementById('global-error-feedback');
    if (!errorMessageDiv) {
      errorMessageDiv = document.createElement('div');
      errorMessageDiv.id = 'global-error-feedback';
      errorMessageDiv.className = 'feedback-container global';
      errorMessageDiv.innerHTML =
        '<i class="fas fa-times-circle feedback-icon" aria-hidden="true"></i>' +
        '<span id="global-error-message" class="feedback-message"></span>';
      document.body.appendChild(errorMessageDiv);
    }
    errorMessageSpan = errorMessageDiv.querySelector('#global-error-message');
  }

  if (message) {
    errorMessageSpan.textContent = message;
  }

  errorMessageDiv.classList.add('visible');

  // Hide the checkmark after 2 seconds
  setTimeout(() => {
    errorMessageDiv.classList.remove('visible');
  }, 2000); // 2 seconds
}

const PROCESSING_ERROR_NOTICE_TITLE = 'We hit a snag';

function getProcessingErrorToastElements() {
  if (processingErrorToastElements) {
    return processingErrorToastElements;
  }

  const body = document.body;
  if (!body) {
    return null;
  }

  const toast = document.createElement('div');
  toast.id = 'processing-error-toast';
  toast.className = 'processing-error-toast';
  toast.setAttribute('role', 'alert');
  toast.setAttribute('aria-live', 'assertive');
  toast.setAttribute('aria-atomic', 'true');
  toast.setAttribute('aria-hidden', 'true');

  toast.innerHTML = `
    <div class="processing-error-icon" aria-hidden="true">
      <i class="fas fa-exclamation-circle"></i>
    </div>
    <div class="processing-error-body">
      <h2 class="processing-error-title">${PROCESSING_ERROR_NOTICE_TITLE}</h2>
      <p class="processing-error-message"></p>
    </div>
    <button type="button" class="processing-error-close" aria-label="Dismiss notification">
      <span aria-hidden="true">&times;</span>
    </button>
  `;

  body.appendChild(toast);

  const closeButton = toast.querySelector('.processing-error-close');
  const messageElement = toast.querySelector('.processing-error-message');

  if (closeButton) {
    closeButton.addEventListener('click', hideProcessingErrorNotice);
  }

  toast.addEventListener('mouseenter', () => {
    if (processingErrorToastHideTimeoutId) {
      clearTimeout(processingErrorToastHideTimeoutId);
      processingErrorToastHideTimeoutId = null;
    }
  });

  toast.addEventListener('mouseleave', () => {
    if (processingErrorToastHideTimeoutId) {
      clearTimeout(processingErrorToastHideTimeoutId);
    }

    if (toast.classList.contains('visible')) {
      processingErrorToastHideTimeoutId = window.setTimeout(
        hideProcessingErrorNotice,
        8000
      );
    }
  });

  toast.addEventListener('transitionend', (event) => {
    if (event.propertyName === 'opacity' && !toast.classList.contains('visible')) {
      toast.setAttribute('aria-hidden', 'true');
    }
  });

  processingErrorToastElements = {
    toast,
    messageElement,
    closeButton,
  };

  return processingErrorToastElements;
}

function hideProcessingErrorNotice() {
  if (!processingErrorToastElements) {
    return;
  }

  const { toast } = processingErrorToastElements;

  if (processingErrorToastHideTimeoutId) {
    clearTimeout(processingErrorToastHideTimeoutId);
    processingErrorToastHideTimeoutId = null;
  }

  toast.classList.remove('visible');
  toast.setAttribute('aria-hidden', 'true');

  if (processingErrorToastKeydownHandler) {
    document.removeEventListener('keydown', processingErrorToastKeydownHandler);
    processingErrorToastKeydownHandler = null;
  }
}

function showProcessingErrorNotice(message) {
  const elements = getProcessingErrorToastElements();

  if (!elements) {
    alert(message);
    return;
  }

  const { toast, messageElement, closeButton } = elements;

  if (!toast || !messageElement) {
    alert(message);
    return;
  }

  messageElement.textContent = message;

  toast.classList.add('visible');
  toast.setAttribute('aria-hidden', 'false');

  if (processingErrorToastHideTimeoutId) {
    clearTimeout(processingErrorToastHideTimeoutId);
  }

  processingErrorToastHideTimeoutId = window.setTimeout(
    hideProcessingErrorNotice,
    8000
  );

  if (!processingErrorToastKeydownHandler) {
    processingErrorToastKeydownHandler = (event) => {
      if (event.key === 'Escape' || event.key === 'Esc') {
        hideProcessingErrorNotice();
      }
    };
    document.addEventListener('keydown', processingErrorToastKeydownHandler);
  }

}

function setupSettingsModal(headers, collectionId, isPublic, collection_name) {
  const editButton = document.getElementById('edit-collection');
  const shareButton = document.getElementById('copy-collection-link');
  const copyButton = document.getElementById('copy-collection-url');
  const settingsModal = document.getElementById('settingsModal');
  const closeModal = settingsModal.querySelector('.close');
  const publicToggle = document.getElementById('public-toggle');
  const collectionNameInput = document.getElementById('collection-name-input');
  const saveNameButton = document.getElementById('save-name-button');
  const deleteButton = document.getElementById('delete-collection-button');

  let currentCollectionId = collectionId;

  collectionNameInput.value = collection_name;
  const activeTab = document.querySelector('.collection-tab.active');
  if (activeTab) activeTab.textContent = collection_name;

  if (shareButton && !shareButton.dataset.setup) {
    shareButton.addEventListener('click', async () => {
      const active = document.querySelector('.collection-tab.active');
      if (!active) return;
      currentCollectionId = Number(active.dataset.collectionId);
      const username =
        (stateManager.getState().serverside_user || {}).username || '';
      const link = `${window.location.origin}/profile.html?username=${username}&collection=${active.dataset.collectionId}`;
      const title = `${username}'s CardSense collection`;

      try {
        const collections = await getCollections(headers);
        const activeCol = collections.find((c) => c.id === currentCollectionId);
        if (activeCol) {
          isPublic = activeCol.is_public;
        }
      } catch (e) {
        console.error('Failed to fetch collection info', e);
      }

      if (!isPublic) {
        const confirmPublic = confirm(
          'This collection is private.\nWould you like to make it public before sharing?'
        );
        if (!confirmPublic) {
          return;
        }
        try {
          await setCollectionPublic(headers, currentCollectionId, true);
          isPublic = true;
          if (publicToggle) publicToggle.checked = true;
        } catch (err) {
          console.error('Failed to make collection public', err);
          showErrorIcon('Failed to update collection privacy.');
          return;
        }
      }

      shareLink(
        link,
        () => showSuccessIcon('Share link copied.'),
        () => showErrorIcon('Failed to share link.'),
        title
      );
    });
    shareButton.dataset.setup = 'true';
  }

  if (copyButton && !copyButton.dataset.setup) {
    copyButton.addEventListener('click', async () => {
      const active = document.querySelector('.collection-tab.active');
      if (!active) return;
      currentCollectionId = Number(active.dataset.collectionId);
      const username =
        (stateManager.getState().serverside_user || {}).username || '';
      const link = `${window.location.origin}/profile.html?username=${username}&collection=${active.dataset.collectionId}`;

      copyLink(
        link,
        () => showSuccessIcon('Link copied to clipboard.'),
        () => showErrorIcon('Failed to copy link.')
      );
    });
    copyButton.dataset.setup = 'true';
  }

  // Open modal
  editButton.addEventListener('click', async function () {
    const activeTab = document.querySelector('.collection-tab.active');
    if (activeTab) {
      currentCollectionId = Number(activeTab.dataset.collectionId);
      try {
        const collections = await getCollections(headers);
        const activeCol = collections.find((c) => c.id === currentCollectionId);
        if (activeCol) {
          collectionNameInput.value = activeCol.name;
          isPublic = activeCol.is_public;
        }
      } catch (e) {
        console.error('Failed to load collection info', e);
      }
    }
    settingsModal.style.display = 'flex';
    settingsModal.style.flexDirection = 'column';
    publicToggle.checked = isPublic;
  });

  // Close modal
  closeModal.addEventListener('click', function () {
    settingsModal.style.display = 'none';
  });

  // Close modal when clicking outside of it
  window.addEventListener('click', function (event) {
    if (event.target === settingsModal) {
      settingsModal.style.display = 'none';
    }
  });

  // Handle toggle change
  publicToggle.addEventListener('change', function () {
    isPublic = this.checked;
    console.log(`Collection is now ${isPublic ? 'public' : 'private'}`);
    setCollectionPublic(headers, currentCollectionId, isPublic);
  });

  // Handle toggle change
  saveNameButton.addEventListener('click', async function () {
    console.log('Updating collection name');

    const res = await updateCollectionName(
      headers,
      currentCollectionId,
      collectionNameInput.value
    );
    if (res?.name === collectionNameInput.value) {
      showSuccessIcon('Collection name updated successfully.');
      const activeTab = document.querySelector('.collection-tab.active');
      if (activeTab) activeTab.textContent = collectionNameInput.value;
    } else {
      showErrorIcon('Failed to update collection name.');
    }
  });

  deleteButton.addEventListener('click', async function () {
    const confirmDelete = confirm(
      'Are you sure you want to delete this collection? This action cannot be undone.'
    );
    if (!confirmDelete) return;

    try {
      await deleteCollection(headers, currentCollectionId);
      settingsModal.style.display = 'none';

      const collections = await getCollections(headers);
      if (collections.length > 0) {
        const first = collections[0];
        setupCollectionTabs(headers, first.id, first.name);
        await cardGrid.loadCards(headers, first.id);
        window.location.hash = `#/collections/${first.id}`;
      } else {
        document.getElementById('collection-tabs').innerHTML = '';
        const grid = document.querySelector('.card-grid');
        if (grid) grid.innerHTML = '';
      }
    } catch (e) {
      console.error('Failed to delete collection', e);
      if (e && e.message && e.message.includes('Collection not empty')) {
        alert('Collection not empty. Please remove all cards before deleting.');
      }
    }
  });



  // No shareable link displayed in the settings modal
}

function setupCreateCollectionModal(headers) {
  const openBtn = document.getElementById('add-collection');
  const tabBtn = document.getElementById('add-collection-tab');
  const modal = document.getElementById('createCollectionModal');
  if (!openBtn && !tabBtn) return;
  if (!modal) return;

  const closeBtn = modal.querySelector('.close');
  const createBtn = document.getElementById('create-collection-button');
  const nameInput = document.getElementById('new-collection-name');

  const openModal = async () => {
    modal.style.display = 'flex';
    modal.style.flexDirection = 'column';
    if (nameInput) nameInput.value = '';
  };

  if (openBtn && !openBtn.dataset.modalSetup) {
    openBtn.addEventListener('click', openModal);
    openBtn.dataset.modalSetup = 'true';
  }
  if (tabBtn && !tabBtn.dataset.modalSetup) {
    tabBtn.addEventListener('click', openModal);
    tabBtn.dataset.modalSetup = 'true';
  }

  closeBtn.addEventListener('click', () => {
    modal.style.display = 'none';
  });

  window.addEventListener('click', (event) => {
    if (event.target === modal) {
      modal.style.display = 'none';
    }
  });

  createBtn.addEventListener('click', async () => {
    const name = nameInput.value.trim();
    if (!name) return;

    // Prevent accidental double submissions
    createBtn.disabled = true;
    const originalText = createBtn.textContent;
    createBtn.textContent = 'Creating...';

    try {
      // Avoid creating duplicate collections by name
      const existing = await getCollections(headers);
      const nameExists = existing.some(
        (c) => c.name.toLowerCase() === name.toLowerCase()
      );
      if (nameExists) {
        showErrorIcon('A collection with that name already exists.');
        return;
      }

      const data = await createCollection(headers, name);
      modal.style.display = 'none';
      if (data?.id) {
        // Rebuild the tabs with the new collection and load its cards
        setupCollectionTabs(headers, data.id, name);
        await cardGrid.loadCards(headers, data.id);
        // Update the URL hash to reflect the new collection
        window.location.hash = `#/collections/${data.id}`;
      }
    } catch (e) {
      console.error('Failed to create collection', e);
      showErrorIcon('Failed to create collection.');
    } finally {
      createBtn.disabled = false;
      createBtn.textContent = originalText;
    }
  });
}

function setupCollectionTabs(headers, currentId, collectionName) {
  const container = document.getElementById('collection-tabs');
  if (!container) return;
  container.innerHTML = '';

  const createTab = (collection) => {
    const btn = document.createElement('button');
    btn.className = 'collection-tab dynamic';
    btn.innerHTML = `${collection.name} <i class="default-star far fa-star" title="Set Default Collection"></i>`;
    btn.dataset.collectionId = collection.id;

    const star = btn.querySelector('.default-star');
    const { defaultCollection } = stateManager.getState();
    if (defaultCollection && defaultCollection.id === collection.id) {
      star.classList.remove('far');
      star.classList.add('fas', 'active');
    }

    star.addEventListener('click', async (e) => {
      e.stopPropagation();
      try {
        await setDefaultCollection(headers, collection.id);
        document.querySelectorAll('.default-star').forEach((s) => {
          s.classList.remove('fas', 'active');
          s.classList.add('far');
        });
        star.classList.remove('far');
        star.classList.add('fas', 'active');
        showSuccessIcon('Collection set as default successfully.');
      } catch (e) {
        console.error('Failed to set default collection', e);
        showErrorIcon('Failed to set default collection.');
      }
    });

    if (collection.id === currentId) btn.classList.add('active');
    btn.addEventListener('click', async () => {
      document
        .querySelectorAll('.collection-tab')
        .forEach((t) => t.classList.remove('active'));
      btn.classList.add('active');
      await cardGrid.loadCards(headers, collection.id);
    });
    return btn;
  };

  const appendAddTab = () => {
    const addTab = document.createElement('button');
    addTab.className = 'collection-tab dynamic add-tab';
    addTab.id = 'add-collection-tab';
    addTab.title = 'Create New Collection';
    addTab.innerHTML = '<i class="fas fa-plus"></i>';
    container.appendChild(addTab);
    setupCreateCollectionModal(headers);
  };

  if (canUseMultiCollection()) {
    getCollections(headers)
      .then((collections) => {
        collections.forEach((c) => container.appendChild(createTab(c)));
        appendAddTab();
      })
      .catch((e) => console.error('Failed to load collections', e));
  } else {
    const single = { id: currentId, name: collectionName || 'My Collection' };
    container.appendChild(createTab(single));
    appendAddTab();
  }
}

async function setCollectionPublic(headers, collectionId, isPublic) {
  try {
    const data = await updateCollectionPrivacy(headers, collectionId, isPublic);
    console.log('Collection updated successfully:', data);
    // Update your UI accordingly, for example:
    // Show a message, or toggle a UI element to reflect the new public/private state.
  } catch (error) {
    console.error('Network error:', error);
  }
}

async function handleAuthenticatedUser(user, token) {
  try {
    const headers = setupUserToken(token);
    let desktopContainer = document.querySelector(
      '.desktop-dropzone-container'
    );
    warmup();

    const newHeaders = { ...headers, 'Content-Type': 'application/json' };
    const payload = {};

    fetchCardData(
      `${config.detectionUrl}/refresh-user-comps`,
      'POST',
      newHeaders,
      payload
    );

    let serverside_user = await getUser(headers);
    let shouldStartFirstScan = false;

    stateManager.updateState({
      serverside_user: serverside_user,
    });

    // Determine subscription level before checking multi-collection access
    await setupLifetimeBanner(headers);

    if (!serverside_user.username || serverside_user.username.trim() == '') {
      const setupResult = await showProfileSetupWizardAndWait(token);
      shouldStartFirstScan = setupResult?.startFirstScan === true;

      serverside_user = await getUser(headers);
      stateManager.updateState({ serverside_user: serverside_user });
    }

    if (serverside_user.has_seen_tour) {
      startTour.disable();
      startTourMobile.disable();
      tour.disable();

      stateManager.updateState({
        tour1Started: false,
      });
    } else {
      stateManager.updateState({
        tour1Started: true,
      });
    }

    const defaultCol = serverside_user.default_collection || {};
    const defaultCollectionId =
      defaultCol.id !== undefined && defaultCol.id !== null
        ? defaultCol.id
        : serverside_user.collection_id;
    const previewLink = document.getElementById('profile-preview-link');

    if (previewLink && serverside_user.username) {
      const encodedUsername = encodeURIComponent(serverside_user.username);
      let previewHref = `/profile.html?username=${encodedUsername}`;

      if (defaultCollectionId !== undefined && defaultCollectionId !== null) {
        previewHref += `&collection=${encodeURIComponent(defaultCollectionId)}`;
      }

      previewLink.href = previewHref;
    }

    setupSettingsModal(
      headers,
      defaultCollectionId,
      defaultCol.is_public || serverside_user.is_collection_public,
      defaultCol.name || serverside_user.collection_name
    );
    if (!canUseMultiCollection()) {
      const addBtn = document.getElementById('add-collection');
      if (addBtn) addBtn.style.display = 'none';
    }
    setupCollectionTabs(
      headers,
      defaultCol.id || serverside_user.collection_id,
      defaultCol.name || serverside_user.collection_name
    );

    if (!isMobileDevice()) {
      await updateTourStatus(headers, { has_seen_tour: true });

      desktopContainer.style.display = 'flex';
      setupDropzonesWithEventListeners(
        headers,
        '#desktop-dropzone1'
      );
      await cardGrid.loadCards(
        headers,
        defaultCol.id || serverside_user.collection_id
      );
      setupStateSubscriptions(headers);
      logMessage(
        LOG_LEVELS.INFO,
        'User authenticated and handlers setup complete.'
      );

      if (shouldStartFirstScan) {
        desktopContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      startTour.start();
    } else {
      await updateTourStatus(headers, { has_seen_tour: true });

      stateManager.updateState({ isMobileDevice: true });
      desktopContainer.style.display = 'none';

      setupDropzonesWithEventListeners(
        headers,
        '#mobile-dropzone1'
      );

      await cardGrid.loadCards(
        headers,
        defaultCol.id || serverside_user.collection_id
      );

      var plus = document.getElementById('plus-icon');
      plus.style.display = 'block';
      const openScanModal = setupCardModal(headers);
      setupMobileStateSubscriptions(headers);

      if (shouldStartFirstScan && openScanModal) {
        openScanModal();
      } else {
        startTourMobile.start();
      }
    }
  } catch (error) {
    logMessage(LOG_LEVELS.ERROR, 'Error handling authenticated user', {
      error: error.message,
    });
  }
}

async function handleUnauthenticatedUser() {
  window.location.href = '/login.html';
}

function setupCardModal() {
  const plusIcon = document.getElementById('plus-icon');
  const modal = document.getElementById('scanModal');

  if (!plusIcon || !modal) {
    return null;
  }

  const closeBtn = modal.querySelector('.close');
  const heroScanButton = document.getElementById('start-scan-button');

  const openScanModal = () => {
    warmup();
    startTourMobile.endTour();
    startTourMobile.disable();

    modal.style.display = 'flex';
    stateManager.updateState({ popupOnDisplay: true });
    mobileCameraCapture?.start();
  };

  // Open modal from floating action button
  plusIcon.addEventListener('click', openScanModal);

  // Support opening the modal from the hero button on mobile
  if (heroScanButton) {
    heroScanButton.addEventListener('click', (event) => {
      event.preventDefault();
      openScanModal();
    });
  }

  // Close via ✕
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      mobileCameraCapture?.stop();
      modal.style.display = 'none';
      stateManager.updateState({ reinitialize: true });
    });
  }

  // Optional: click outside to close
  modal.addEventListener('click', (event) => {
    if (event.target === modal) {
      mobileCameraCapture?.stop();
      modal.style.display = 'none';
      stateManager.updateState({ reinitialize: true });
    }
  });

  return openScanModal;
}

async function warmup() {
  try {
    await sendAuthenticatedRequest(`${config.detectionUrl}/warmup`, {
      method: 'POST',
    });
  } catch (error) {
    console.log(error.message);
  }
}

function setupDropzonesWithEventListeners(headers, front_dropzone_id) {
  try {
    dropzone_manager1 = setupDropzone(
      headers,
      front_dropzone_id,
      `${config.detectionUrl}/front-image`,
      isMobileDevice()
        ? 'Choose a photo containing one card front'
        : 'Upload an image containing one card front'
    );
    let dropzone1 = dropzone_manager1.instance();

    if (isMobileDevice()) {
      mobileCameraCapture = new MobileCameraCapture(dropzone_manager1);
    }

    let dropzone1SuccessHandler = createDropzoneSuccessHandler(
      handleCardFrontsSuccess,
      headers
    );

    attachDropzoneSuccessListener(dropzone1, dropzone1SuccessHandler, headers);

    if (isMobileDevice()) {
      attachMobileSubmitButtonEventListeners(dropzone_manager1);
    } else {
      attachDesktopSubmitButtonEventListeners(dropzone_manager1);
    }

    return [dropzone1];
  } catch (error) {
    logMessage(
      LOG_LEVELS.ERROR,
      'Error setting up dropzones with event listeners',
      { error: error.message }
    );
  }
}

function createDropzoneSuccessHandler(successCallback, headers) {
  return async function (response, local_file) {
    try {
      logMessage(LOG_LEVELS.INFO, 'Processing initiated.', {
        requestId: response.request_id,
      });
      const requestId = response.request_id;
      const controller = new AbortController();

      const localFileUrl = URL.createObjectURL(local_file);

      stateManager.updateState({
        currentFrontRequestId: requestId,
        currentFrontImage: localFileUrl,
      });
      abortControllers.push(controller);
      await fetchStreamingData(
        `${config.detectionUrl}/get-data-stream/${requestId}`,
        headers,
        (data) => successCallback(data),
        controller.signal
      );
    } catch (error) {
      logMessage(LOG_LEVELS.ERROR, 'Error in dropzone success handler', {
        error: error.message,
      });
    }
  };
}

async function setupLifetimeBanner(headers) {
  const data = await getSubscriptionLevel(headers);
  const level = data.subscription_level;
  const lifetime_number = data.lifetime_number;

  const membershipBanner = document.querySelector('[id^="membership-banner"]');
  const lifetimeBanner = document.querySelector('[id^="lifetime-banner"]');

  if (membershipBanner) {
    membershipBanner.style.display = 'none';
  }

  if (lifetimeBanner) {
    lifetimeBanner.style.display = 'none';
  }

  if (level === 'lifetime') {
    if (lifetimeBanner) {
      lifetimeBanner.style.display = 'flex';

      const paragraph = lifetimeBanner.querySelector('p');
      if (paragraph) {
        paragraph.textContent = lifetime_number + '/149';
      }
    }
  }

  stateManager.updateState({ subscription_level: level });
}

function setupStateSubscriptions(headers) {
  try {
    stateManager.subscribe((newState) =>
      handleStateChangeForReinitialization(newState, headers)
    );
    stateManager.subscribe((newState) =>
      handleStateChangeForCardInteraction(newState, headers)
    );
    stateManager.subscribe((newState) =>
      handleStateChangeForStopProcessing(newState)
    );
    stateManager.subscribe(handleStateChangeForDropzoneFiles);
    stateManager.subscribe((newState, oldState) =>
      handleStateChangeForErrorMode(newState, oldState, headers)
    );
    logMessage(LOG_LEVELS.INFO, 'State subscriptions setup complete.');
  } catch (error) {
    logMessage(LOG_LEVELS.ERROR, 'Error setting up state subscriptions', {
      error: error.message,
    });
  }
}

function setupMobileStateSubscriptions(headers) {
  try {
    stateManager.subscribe((newState) =>
      handleStateChangeForReinitialization(newState, headers)
    );
    stateManager.subscribe((newState) =>
      handleStateChangeForCardInteraction(newState, headers)
    );
    stateManager.subscribe((newState) =>
      handleStateChangeForStopProcessing(newState)
    );
    stateManager.subscribe((newState) =>
      handleStateChangeForDisplayFront(newState)
    );
    stateManager.subscribe((newState) =>
      handleStateChangeForDropzoneFiles(newState)
    );
    stateManager.subscribe((newState, oldState) =>
      handleStateChangeForErrorMode(newState, oldState, headers)
    );
    logMessage(LOG_LEVELS.INFO, 'Mobile state subscriptions setup complete.');
  } catch (error) {
    logMessage(
      LOG_LEVELS.ERROR,
      'Error setting up mobile state subscriptions',
      { error: error.message }
    );
  }
}

function handleStateChangeForDropzoneFiles(newState) {
  try {
    if (isMobileDevice()) {
      if (newState.dropzoneFiles >= 1) {
        enableSubmitButton('submit-mobile');
      } else {
        disableSubmitButton('submit-mobile');
      }
    } else {
      if (newState.dropzoneFiles > 0) {
        enableSubmitButton('submit-desktop');
        startTour.setStepElement(
          DESKTOP_TOUR_STEP_INDEX,
          DESKTOP_TOUR_WORKSPACE_ELEMENT_ID
        );
      } else if (newState.dropzoneFiles <= 0) {
        disableSubmitButton('submit-desktop');
        startTour.setStepElement(
          DESKTOP_TOUR_STEP_INDEX,
          DESKTOP_TOUR_DEFAULT_ELEMENT_ID
        );
      }
    }
  } catch (error) {
    logMessage(
      LOG_LEVELS.ERROR,
      'Error in state change for Dropzone Files changes',
      { error: error.message }
    );
  }
}
// Handles reinitializing the application based on state changes
function handleStateChangeForReinitialization(newState, headers) {
  try {
    if (newState.reinitialize) {
      logMessage(LOG_LEVELS.INFO, 'Reinitializing.');
      reinitialize(headers);
    }
  } catch (error) {
    logMessage(LOG_LEVELS.ERROR, 'Error in state change for reinitialization', {
      error: error.message,
    });
  }
}

// Handles updating UI based on card interaction state changes
function handleStateChangeForCardInteraction(newState, headers) {
  try {
    const {
      firstCard,
      clickActionTaken,
      frontStreamDone,
      currentFrontRequestId,
    } = newState;

    if (
      stateManager.hasCard(currentFrontRequestId) &&
      (firstCard || clickActionTaken)
    ) {
      const { currentCardFront } = stateManager.getNextCard(
        currentFrontRequestId
      );
      const newCardInfo = getBestCardInfo(currentCardFront);

      if (firstCard) {
        stateManager.updateState({
          firstCard: false,
          newCardArrived: false,
          clickActionTaken: false,
        });
        hideOverlay();
        document.body.classList.add('body-no-scroll');

        logMessage(LOG_LEVELS.INFO, 'Got first card. Creating popup.');
        form_popup_controller.createAndShowPopup(
          currentCardFront,
          newCardInfo,
          headers
        );
      } else if (clickActionTaken) {
        form_popup_controller.updatePopup(
          currentCardFront,
          newCardInfo,
          headers
        );
        stateManager.updateState({
          newCardArrived: false,
          clickActionTaken: false,
        });
      }
    } else if (
      frontStreamDone &&
      !stateManager.hasCard(currentFrontRequestId) &&
      clickActionTaken
    ) {
      document.body.classList.remove('body-no-scroll');
      stateManager.updateState({ reinitialize: true });
    }
  } catch (error) {
    logMessage(LOG_LEVELS.ERROR, 'Error in state change for card interaction', {
      error: error.message,
    });
    document.body.classList.remove('body-no-scroll');

    stateManager.updateState({ stopProcessing: true });
  }
}

// Handles stopping processing based on state changes
async function handleStateChangeForStopProcessing(newState) {
  try {
    if (newState.stopProcessing) {
      abortAllRequests();
      stateManager.updateState({ reinitialize: true });
    }
  } catch (error) {
    logMessage(LOG_LEVELS.ERROR, 'Error in state change for stop processing', {
      error: error.message,
    });
  }
}

// Handles stopping processing based on state changes
function handleStateChangeForDisplayFront(newState) {
  try {
    if (newState.popupOnDisplay) {
      var plus = document.getElementById('plus-icon');
      plus.style.display = 'none';
    } else {
      var plus = document.getElementById('plus-icon');
      plus.style.display = 'block';
    }
  } catch (error) {
    logMessage(LOG_LEVELS.ERROR, 'Error in state change for stop processing', {
      error: error.message,
    });
  }
}

let isHandlingErrorState = false; // Flag to track if the function is already processing an error state
let stateChangeQueue = []; // Queue to store state changes

function handleStateChangeForErrorMode(newState, oldState, headers) {
  try {
    const shouldResetErrorHandling =
      (newState.stopProcessing && !oldState.stopProcessing) ||
      (newState.reinitialize && !oldState.reinitialize);

    if (shouldResetErrorHandling) {
      isHandlingErrorState = false;
      stateChangeQueue = [];
    }

    if (isHandlingErrorState) {
      console.log('pushing new state');
      stateChangeQueue.push({ newState, oldState, headers }); // Add to queue if already handling an error state
      return;
    }

    const handlePopupClose = () => {
      isHandlingErrorState = false; // Clear the flag after the user is done

      // Check if there are pending state changes in the queue
      if (stateChangeQueue.length > 0) {
        const nextStateChange = stateChangeQueue.shift(); // Get the next state change
        handleStateChangeForErrorMode(
          nextStateChange.newState,
          nextStateChange.oldState,
          nextStateChange.headers
        ); // Process the next state change
      }
    };

    if (newState.frontInErrorState && !oldState.frontInErrorState) {
      const userDecision = confirm(
        'We are unable to detect a card on the front side. Would you like to manually set the corners?'
      );
      if (userDecision) {
        console.log('in front error state');
        isHandlingErrorState = true; // Set the flag
        hideOverlay();
        const local_parent_image = newState.currentFrontImage;
        const server_parent_id = extractIdFromUrl(newState.front_parent_url);
        const corner_popup = new FixCornersController(
          'front',
          local_parent_image,
          server_parent_id
        );
        corner_popup.bindCompletionCallback(handleCardFrontsSuccess);
        corner_popup.createAndShowPopup(headers);
        corner_popup.bindOnClose(handlePopupClose); // Set the callback for when the popup is closed
        stateManager.updateState({ frontInErrorState: false });
      } else {
        alert('Cancelled Scan.');
        stateManager.updateState({ stopProcessing: true });
      }
    } else if (stateChangeQueue.length > 0) {
      const nextStateChange = stateChangeQueue.shift(); // Get the next state change
      handleStateChangeForErrorMode(
        nextStateChange.newState,
        nextStateChange.oldState,
        nextStateChange.headers
      ); // Process the next state change
    }
  } catch (error) {
    isHandlingErrorState = false; // Ensure the flag is cleared on error
    logMessage(LOG_LEVELS.ERROR, 'Error in state change for error mode', {
      error: error.message,
    });

    // Check if there are pending state changes in the queue
    if (stateChangeQueue.length > 0) {
      const nextStateChange = stateChangeQueue.shift(); // Get the next state change
      handleStateChangeForErrorMode(
        nextStateChange.newState,
        nextStateChange.oldState,
        nextStateChange.headers
      ); // Process the next state change
    }
  }
}

// Aborts all ongoing fetch requests
function abortAllRequests() {
  try {
    logMessage(
      LOG_LEVELS.INFO,
      'Number of abort controllers: ',
      abortControllers.length
    );
    abortControllers.forEach(async (controller) => await controller.abort());
    abortControllers = []; // Clear the controllers array after aborting requests
    logMessage(LOG_LEVELS.INFO, 'All ongoing fetch requests aborted.');
  } catch (error) {
    logMessage(LOG_LEVELS.ERROR, 'Error aborting all requests', {
      error: error.message,
    });
  }
}

const DEFAULT_PROCESSING_ERROR_MESSAGE =
  "We're sorry—an unexpected error occurred while processing your cards. Our team has been notified and is looking into it. Please try again soon.";

function extractProcessingErrorMessage(data) {
  if (!data || typeof data !== 'object') {
    return null;
  }

  if (data.status === 'error' || data.type === 'error') {
    if (typeof data.error === 'string' && data.error.trim()) {
      return data.error;
    }
    if (typeof data.error_message === 'string' && data.error_message.trim()) {
      return data.error_message;
    }
    if (typeof data.message === 'string' && data.message.trim()) {
      return data.message;
    }
    if (typeof data.detail === 'string' && data.detail.trim()) {
      return data.detail;
    }
    return DEFAULT_PROCESSING_ERROR_MESSAGE;
  }

  if (typeof data.error === 'string' && data.error.trim()) {
    return data.error;
  }

  if (typeof data.error_message === 'string' && data.error_message.trim()) {
    return data.error_message;
  }

  if (
    data.error &&
    typeof data.error === 'object' &&
    typeof data.error.message === 'string' &&
    data.error.message.trim()
  ) {
    return data.error.message;
  }

  return null;
}

function handleProcessingFailure(message) {
  const { stopProcessing } = stateManager.getState();

  if (stopProcessing) {
    return;
  }

  const userMessage = message || DEFAULT_PROCESSING_ERROR_MESSAGE;

  logMessage(LOG_LEVELS.ERROR, 'Card processing failed', {
    message: userMessage,
  });

  hideOverlay();
  stateManager.updateState({
    stopProcessing: true,
    alertOnMismatch: false,
  });
  showProcessingErrorNotice(userMessage);
}

function checkAndHandleProcessingError(data) {
  const message = extractProcessingErrorMessage(data);
  if (!message) {
    return false;
  }

  handleProcessingFailure(message);
  return true;
}

function handleCardFrontsSuccess(data) {
  const { currentFrontRequestId, stopProcessing } =
    stateManager.getState();

  if (
    !data.hasOwnProperty('error_code') &&
    checkAndHandleProcessingError(data)
  ) {
    return;
  }

  if (data.hasOwnProperty('error_code')) {
    let error_message = 'Unknown error.';

    if (data.error_code == -1) {
      error_message =
        'More than one card was detected. Please submit an image containing only one card.';
      alert(error_message);
      stateManager.updateState({ stopProcessing: true });
    } else if (data.error_code == -2) {
      stateManager.updateState({
        frontInErrorState: true,
        front_parent_url: data.image_data.parent_image_url,
      });
    } else {
      const message =
        extractProcessingErrorMessage(data) || DEFAULT_PROCESSING_ERROR_MESSAGE;
      handleProcessingFailure(message);
    }
  }

  // Check if data has the card_front_count key
  else if (data.hasOwnProperty('card_front_count')) {
    if (data.card_front_count > 1) {
      handleProcessingFailure(
        'More than one card was detected. Please submit an image containing only one card.'
      );
      return;
    }

    stateManager.updateState({
      currentCardFrontCount: data.card_front_count,
      front_parent_url: data.image_data.parent_image_url,
    });
  } else {
    if (!stopProcessing) {
      stateManager.addCardFronts(currentFrontRequestId, data.card_front);
      stateManager.updateState({ newCardArrived: true });
    }
  }
}

function getBestCardInfo(front) {
  return { ...front };
}

function closeAlert() {
  document.getElementById('upgradeModal').style.display = 'none';
}

function upgradeAccount() {
  closeAlert();
}

function attachDesktopSubmitButtonEventListeners(dropzone1) {
  document
    .getElementById('upgradeAccountButton')
    .addEventListener('click', upgradeAccount);
  document
    .getElementById('closeAlertButton')
    .addEventListener('click', closeAlert);
  document
    .getElementById('noThanksButton')
    .addEventListener('click', closeAlert);

  const submitButton = document.getElementById('submit-desktop');
  submitButton.addEventListener('click', function () {
    startTour.endTour();
    showOverlay();

    const skipCornerDetection = document.getElementById(
      'skip-corner-detection-desktop'
    ).checked; // Check if the checkbox is checked
    const frontRotation = dropzone_manager1.getRotation();

    stateManager.updateState({ frontRotation: frontRotation });

    dropzone1.processFiles(skipCornerDetection); // Pass the checkbox state to processFiles
  });
}

function attachMobileSubmitButtonEventListeners(dropzone1) {
  document
    .getElementById('upgradeAccountButton')
    .addEventListener('click', upgradeAccount);
  document
    .getElementById('closeAlertButton')
    .addEventListener('click', closeAlert);
  document
    .getElementById('noThanksButton')
    .addEventListener('click', closeAlert);

  const submitButton = document.getElementById('submit-mobile');
  submitButton.addEventListener('click', function () {
    stateManager.updateState({ frontRotation: 0 });

    showOverlay();
    dropzone1.processFiles(true);
  });
}

function enableSubmitButton(id) {
  const submitButton = document.getElementById(id);
  submitButton.disabled = false;
}

function disableSubmitButton(id) {
  const submitButton = document.getElementById(id);
  submitButton.disabled = true;
}

async function reinitialize(headers) {
  try {
    mobileCameraCapture?.stop();
    form_popup_controller.destroy();
    form_popup_controller = new FormPopupController(cardGrid);
    var modal = document.getElementById('scanModal');
    modal.style.display = 'none';
    hideOverlay();
    document.body.classList.remove('body-no-scroll');

    // Reset relevant states
    const stateUpdate = {
      frontImageSelected: false,
      dropzoneFiles: 0,
      currentCardFrontCount: null,
      reinitialize: false,
      stopProcessing: false,
      alertOnMismatch: false,
      firstCard: true, // Signal that reinitialization should occur
      currentCardFront: null, // Reset currentCardFront
      popupOnDisplay: false,
      currentFrontCoords: null,
      userModified: false,
      inRescan: false,
      frontInErrorState: false,
      clickActionTaken: false,
      phaseOneData: null,
      inPhase2: false,
      frontStreamDone: false,
    };

    stateManager.updateState(stateUpdate);
    document.body.style.position = '';

    const activeTab = document.querySelector('.collection-tab.active');
    const activeId = activeTab ? Number(activeTab.dataset.collectionId) : null;
    cardGrid.loadCards(headers, activeId);

    logMessage(LOG_LEVELS.INFO, 'Application reinitialized.');
  } catch (error) {
    logMessage(LOG_LEVELS.ERROR, 'Error during reinitialization', {
      error: error.message,
    });
  }
}

function setupSearchFilter() {
  const searchInput = document.getElementById('cardSearch');
  if (!searchInput) return;
  searchInput.addEventListener('input', (e) => {
    cardGrid.filterCards(e.target.value);
  });
}

function isMobileDevice() {
  let check = false;
  // Patterns to match against user agent for common mobile devices
  (function (a) {
    if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(a)
    ) {
      check = true;
    }
  })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
}
